import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useAudioCapture, AudioChunk } from '../hooks/useAudioCapture';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { AudioError, ConnectionError, StatusIndicator } from './ErrorDisplay';
import { AudioErrorBoundary, WebSocketErrorBoundary } from './ErrorBoundary';
import { errorHandler, performanceMonitor } from '../lib/errorHandler';
import {
  Mic,
  MicOff,
  Play,
  Square,
  Wifi,
  WifiOff,
  Copy,
  Share2,
  AlertCircle,
  Volume2,
  Users
} from 'lucide-react';
import { cn } from '../lib/utils';

interface StreamerInterfaceProps {
  className?: string;
}


export const StreamerInterface: React.FC<StreamerInterfaceProps> = ({ className }) => {
  const [roomId, setRoomId] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [viewerCount] = useState(0);
  const [copiedRoomId, setCopiedRoomId] = useState(false);

  // Use ref to track streaming state for immediate access
  const isStreamingRef = useRef(false);

  // Generate room ID on component mount
  useEffect(() => {
    const generateRoomId = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let result = '';
      for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      console.log('🎲 Generated new room ID:', result);
      return result;
    };

    console.log('🏠 Room ID check:', { currentRoomId: roomId, needsGeneration: !roomId });

    if (!roomId) {
      const newRoomId = generateRoomId();
      console.log('🏠 Setting new room ID:', newRoomId);
      setRoomId(newRoomId);
    }
  }, [roomId]);

  // WebSocket connection using react-use-websocket
  const baseWsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8787';
  const socketUrl = roomId ? `${baseWsUrl}/ws/${roomId}` : null;

  console.log('🔗 WebSocket URL:', { socketUrl, roomId, baseWsUrl });

  const {
    sendMessage,
    lastMessage,
    readyState
  } = useWebSocket(socketUrl, {
    shouldReconnect: () => true,
    reconnectAttempts: 10,
    reconnectInterval: 3000,
    onOpen: () => {
      console.log('🟢 WebSocket onOpen event', { roomId, socketUrl });
    },
    onClose: (event) => {
      console.log('🔴 WebSocket onClose event', {
        roomId,
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
    },
    onError: (event) => {
      console.error('❌ WebSocket onError event', { roomId, event });
    }
  });

  // Connection state
  const connectionStatus = {
    [ReadyState.CONNECTING]: 'connecting',
    [ReadyState.OPEN]: 'connected',
    [ReadyState.CLOSING]: 'disconnected',
    [ReadyState.CLOSED]: 'disconnected',
    [ReadyState.UNINSTANTIATED]: 'disconnected'
  }[readyState] as 'connecting' | 'connected' | 'disconnected';

  const isConnected = readyState === ReadyState.OPEN;

  // Handle audio chunks and send via WebSocket
  const handleAudioChunk = useCallback((chunk: AudioChunk) => {
    const endTimer = performanceMonitor.startTimer('audio_chunk_processing');

    console.log('🎵 Audio chunk received:', {
      chunkSize: chunk.data.size,
      sequenceId: chunk.sequenceId,
      isConnected,
      isStreamingRef: isStreamingRef.current,
      readyState
    });

    // Simple connection and streaming check
    if (isConnected && isStreamingRef.current) {
      console.log('🎯 Sending audio chunk...');

      // Convert Blob to ArrayBuffer and send
      chunk.data.arrayBuffer().then(arrayBuffer => {
        try {
          console.log('📤 About to send audio chunk:', {
            size: arrayBuffer.byteLength,
            sequenceId: chunk.sequenceId,
            timestamp: chunk.timestamp,
            roomId,
            socketUrl,
            readyState,
            connectionStatus,
            dataType: 'ArrayBuffer'
          });

          // Send binary data directly using react-use-websocket
          sendMessage(arrayBuffer);

          console.log('✅ Audio chunk sent successfully:', {
            size: arrayBuffer.byteLength,
            sequenceId: chunk.sequenceId,
            roomId
          });
        } catch (error) {
          errorHandler.handleWebSocketError(
            error instanceof Error ? error : new Error('Failed to send audio chunk'),
            {
              component: 'StreamerInterface',
              operation: 'send_audio_chunk',
              metadata: { chunkSize: arrayBuffer.byteLength, sequenceId: chunk.sequenceId }
            }
          );
        }
        endTimer();
      }).catch(error => {
        endTimer();
        errorHandler.handleAudioError(error, {
          component: 'StreamerInterface',
          operation: 'audio_chunk_conversion'
        });
      });
    } else {
      console.log('🚫 Not sending audio chunk:', {
        isConnected,
        isStreamingRef: isStreamingRef.current,
        readyState,
        connectionStatus
      });
      endTimer();
    }

    // Simulate audio level for visualization
    const simulatedLevel = Math.random() * 100;
    setAudioLevel(simulatedLevel);
  }, [isConnected, readyState, connectionStatus, sendMessage]);

  // Audio capture hook
  const audioCapture = useAudioCapture({
    chunkInterval: 1500,
    onAudioChunk: handleAudioChunk,
    onError: (error) => {
      console.error('Audio capture error:', error);
      // Only show error to user if it's not a format support issue during initialization
      // Format support issues are handled by the UI showing disabled state
      if (error.type !== 'FORMAT_UNSUPPORTED') {
        errorHandler.handleAudioError(new Error(error.message), {
          component: 'StreamerInterface',
          operation: 'audio_capture',
          metadata: { errorType: error.type }
        });
      }
    }
  });


  // Monitor WebSocket status changes
  useEffect(() => {
    console.log('WebSocket status changed:', {
      readyState,
      connectionStatus,
      isConnected
    });
  }, [readyState, connectionStatus, isConnected]);

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      console.log('📥 Raw WebSocket message received:', {
        data: lastMessage.data,
        type: typeof lastMessage.data,
        dataLength: lastMessage.data?.length,
        roomId,
        timestamp: Date.now()
      });

      try {
        const message = JSON.parse(lastMessage.data);
        console.log('📥 Parsed WebSocket message:', {
          message,
          messageType: message.type,
          roomId: message.roomId || 'not specified',
          currentRoomId: roomId
        });

        if (message.type === 'status') {
          // Handle status updates, viewer count, etc.
          console.log('📊 Status update received:', message);
        }
      } catch (error) {
        console.error('❌ Failed to parse WebSocket message:', {
          error,
          rawData: lastMessage.data,
          roomId
        });
      }
    }
  }, [lastMessage, roomId]);

  // Start streaming
  const handleStartStream = useCallback(async () => {
    const endTimer = performanceMonitor.startTimer('start_streaming');

    try {
      console.log('🚀 Starting stream...', {
        roomId,
        isConnected,
        readyState,
        connectionStatus
      });

      // Wait for WebSocket connection if not connected
      if (!isConnected) {
        console.log('⏳ Waiting for WebSocket connection...');
        // react-use-websocket handles connection automatically
        // We just need to wait a bit
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (readyState !== ReadyState.UNINSTANTIATED) {
          throw new Error('WebSocket connection failed');
        }
      }

      // Start the stream on the server
      console.log('🎬 Starting stream on server...');
      const startMessage = {
        type: 'start_stream',
        roomId
      };

      console.log('📤 Sending start_stream message:', {
        message: startMessage,
        messageString: JSON.stringify(startMessage),
        currentRoomId: roomId,
        socketUrl,
        readyState
      });

      sendMessage(JSON.stringify(startMessage));
      console.log('✅ Stream started on server!');

      // Start audio capture
      console.log('🎤 Starting audio capture...');
      await audioCapture.startRecording();
      console.log('✅ Audio capture started!');

      setIsStreaming(true);
      isStreamingRef.current = true;

      console.log('🎉 Stream fully started!', {
        readyState,
        isConnected,
        connectionStatus
      });
      endTimer();
    } catch (error) {
      endTimer();
      errorHandler.handleError(
        error instanceof Error ? error : new Error('Failed to start stream'),
        'COMPONENT_ERROR',
        {
          component: 'StreamerInterface',
          operation: 'start_streaming',
          roomId
        }
      );
    }
  }, [isConnected, readyState, connectionStatus, sendMessage, audioCapture, roomId]);

  // Stop streaming
  const handleStopStream = useCallback(() => {
    audioCapture.stopRecording();
    setIsStreaming(false);
    isStreamingRef.current = false;
    setAudioLevel(0);
  }, [audioCapture]);

  // Copy room ID to clipboard
  const handleCopyRoomId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(roomId);
      setCopiedRoomId(true);
      setTimeout(() => setCopiedRoomId(false), 2000);
    } catch (error) {
      console.error('Failed to copy room ID:', error);
    }
  }, [roomId]);

  // Share room ID
  const handleShareRoomId = useCallback(async () => {
    const shareUrl = `${window.location.origin}/viewer?room=${roomId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my live audio stream',
          text: `Join my live audio transcription stream with room ID: ${roomId}`,
          url: shareUrl
        });
      } catch (error) {
        console.error('Failed to share:', error);
        // Fallback to copying URL
        await navigator.clipboard.writeText(shareUrl);
      }
    } else {
      // Fallback to copying URL
      await navigator.clipboard.writeText(shareUrl);
    }
  }, [roomId]);

  // Connection status indicator
  const getConnectionStatusDisplay = () => {
    if (connectionStatus === 'connected') {
      return { icon: Wifi, color: 'text-green-500', text: 'Connected' };
    } else if (connectionStatus === 'connecting') {
      return { icon: Wifi, color: 'text-yellow-500', text: 'Connecting...' };
    } else {
      return { icon: WifiOff, color: 'text-gray-500', text: 'Disconnected' };
    }
  };

  const connectionStatusDisplay = getConnectionStatusDisplay();
  const ConnectionIcon = connectionStatusDisplay.icon;

  return (
    <WebSocketErrorBoundary>
      <AudioErrorBoundary>
        <div className={cn("max-w-2xl mx-auto p-6 space-y-6", className)}>
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold">Audio Streamer</h1>
            <p className="text-muted-foreground">
              Stream your audio with real-time transcription for viewers
            </p>
          </div>

          {/* Connection Error Banner */}
          {connectionStatus === 'disconnected' && readyState === ReadyState.CLOSED && (
            <ConnectionError
              isConnecting={false}
              onRetry={() => window.location.reload()}
            />
          )}

      {/* Room Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Room Information
          </CardTitle>
          <CardDescription>
            Share this room ID with viewers to let them join your stream
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium">Room ID</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-lg tracking-wider">
                  {roomId}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyRoomId}
                  className="shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {copiedRoomId && (
            <div className="text-sm text-green-600 font-medium">
              ✓ Room ID copied to clipboard!
            </div>
          )}

          <Button
            onClick={handleShareRoomId}
            variant="outline"
            className="w-full"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share Room
          </Button>
        </CardContent>
      </Card>

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ConnectionIcon className={cn("h-5 w-5", connectionStatusDisplay.color)} />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Status:</span>
            <StatusIndicator
              status={connectionStatus}
              error={undefined}
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Viewers:</span>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono">{viewerCount}</span>
            </div>
          </div>

          {readyState === ReadyState.CLOSED && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-destructive">Connection Error</div>
                <div className="text-destructive/80">WebSocket connection closed</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audio Controls Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isStreaming ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            Audio Controls
          </CardTitle>
          <CardDescription>
            {audioCapture.isSupported
              ? "Control your audio stream and monitor levels"
              : "WebM/Opus format is not supported in this browser"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Audio Format Support */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Audio Format:</span>
            <Badge variant={audioCapture.isSupported ? 'default' : 'destructive'}>
              {audioCapture.isSupported ? 'WebM/Opus Supported' : 'Not Supported'}
            </Badge>
          </div>

          {/* Audio Level Visualization */}
          {isStreaming && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Audio Level:</span>
                <div className="flex items-center gap-1">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-sm">{Math.round(audioLevel)}%</span>
                </div>
              </div>
              <Progress value={audioLevel} className="h-2" />
            </div>
          )}

          {/* Stream Controls */}
          <div className="flex gap-3">
            {!isStreaming ? (
              <Button
                onClick={handleStartStream}
                disabled={!audioCapture.isSupported || connectionStatus === 'connecting'}
                className="flex-1"
                size="lg"
              >
                <Play className="h-4 w-4 mr-2" />
                Start Streaming
              </Button>
            ) : (
              <Button
                onClick={handleStopStream}
                variant="destructive"
                className="flex-1"
                size="lg"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop Streaming
              </Button>
            )}
          </div>

          {/* Recording Indicator */}
          {isStreaming && (
            <div className="flex items-center justify-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-red-700 font-medium">Live Streaming</span>
            </div>
          )}

          {/* Audio Error Display */}
          {audioCapture.error && (
            <AudioError
              errorType={
                audioCapture.error.type === 'PERMISSION_DENIED' ? 'permission' :
                audioCapture.error.type === 'FORMAT_UNSUPPORTED' ? 'format' :
                'device'
              }
              onRetry={() => audioCapture.startRecording()}
            />
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle>How to Stream</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              1
            </div>
            <div>Share your Room ID with viewers so they can join your stream</div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              2
            </div>
            <div>Click "Start Streaming" and allow microphone access when prompted</div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              3
            </div>
            <div>Speak normally - your audio will be transcribed in real-time for viewers</div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
              4
            </div>
            <div>Monitor your connection status and audio levels during streaming</div>
          </div>
        </CardContent>
      </Card>
        </div>
      </AudioErrorBoundary>
    </WebSocketErrorBoundary>
  );
};
